import { FrequencyRecord, NotesSection, PlayerNote, songBeat } from '~/interfaces';
import { GameStateClass } from '~/modules/game-engine/game-state/game-state';
import { appendFrequencyToPlayerNotes } from '~/modules/game-engine/game-state/helpers/append-frequency-to-player-notes';
import calculateScore, { calculateDetailedScoreData } from '~/modules/game-engine/game-state/helpers/calculate-score';
import InputManager from '~/modules/game-engine/input/input-manager';
import events from '~/modules/game-events/game-events';
import { PlayerNumber } from '~/modules/players/player-number';
import isNotesSection from '~/modules/songs/utils/is-notes-section';
import { getNoteAtBeat } from '~/modules/songs/utils/notes-selectors';

/** How often a phone reads its microphone: `SimplifiedMic` polls every sampleRate / fftSize ms. */
const FREQUENCY_INTERVAL_MS = 44_100 / 2_048;
/** The least a pack is spread over: the gap between two packs, with room for jitter. */
const MIN_PACK_SPAN_MS = 100;

class PlayerState {
  private frequencyRecords: FrequencyRecord[] = [];
  private playerNotes: PlayerNote[] = [];

  // How much of each array the last pack of real frequencies accounted for. Between packs the engine
  // keeps appending the phone's last known frequency every frame so the lane carries on moving; when
  // the next pack lands those guesses are dropped and the frequencies actually sung replayed over
  // them. Remembering a length rewinds in place - the arrays used to be copied whole on every pack,
  // which is tens of thousands of records re-copied hundreds of times across a single song.
  private committedRecordCount = 0;
  private committedNoteCount = 0;
  /** The note the checkpoint falls inside, as it stood when committed - replaying mutates it. */
  private committedBoundaryNote: PlayerNote | null = null;

  private min = Infinity;
  private max = -Infinity;

  private storedSectionIndex = 0;

  public constructor(
    private number: PlayerNumber,
    private gameState: GameStateClass,
  ) {
    const track = this.getTrack();

    // Names itself rather than failing as "Cannot read properties of undefined (reading 'sections')"
    // three lines later. Nothing downstream has to handle a player with no track because this throws
    // before one can exist.
    if (!track) {
      throw new Error(`Player ${number} sings track ${this.getTrackIndex()}, which this song does not have`);
    }

    track.sections.filter(isNotesSection).forEach((section) =>
      section.notes.forEach((note) => {
        this.min = Math.min(this.min, note.pitch);
        this.max = Math.max(this.max, note.pitch);
      }),
    );
  }

  public getNumber = () => this.number;

  public update = () => {
    const currentTime = this.gameState.getCurrentTime();

    const frequency = InputManager.getPlayerFrequency(this.number);
    const currentTimestamp = currentTime - InputManager.getPlayerInputLag(this.number);

    // A pack of frequencies means a remote mic: replay it over the guesses made since the last one.
    // A single frequency is a local microphone, or the guess itself, and just goes on the end.
    if (Array.isArray(frequency)) {
      this.applyFrequencyPack(frequency, currentTimestamp);
      InputManager.clearPlayerCachedFrequencies(this.number);
    } else {
      this.updatePlayerNotes(currentTimestamp, frequency);
    }

    this.dispatchSectionUpdate();
  };

  /**
   * Replays a pack of frequencies from a remote mic in place of the guesses made while waiting for
   * it, spreading them evenly between the last frequency actually sung and now.
   */
  public applyFrequencyPack = (frequencies: number[], currentTimestamp: number) => {
    if (frequencies.length === 0) return;

    // Before the rewind, while the committed record is still in the array. Falls back to the very
    // first record when no pack has landed yet, which is what the guesses start from.
    const lastCommittedRecord = this.frequencyRecords[this.committedRecordCount - 1] ?? this.frequencyRecords.at(0);
    // Capped, or the first pack after a dropout is smeared over the whole of the silence before it.
    const earliestTimestamp = currentTimestamp - Math.max(MIN_PACK_SPAN_MS, frequencies.length * FREQUENCY_INTERVAL_MS);
    // Never after now: a remote mic's lag moves with its ping, and a pack must not run backwards.
    const lastTimestamp = Math.min(currentTimestamp, Math.max(lastCommittedRecord?.timestamp ?? 0, earliestTimestamp));
    const timestampStep = (currentTimestamp - lastTimestamp) / frequencies.length;

    this.rewindToCommitted();

    for (let index = 0; index < frequencies.length; index++) {
      this.updatePlayerNotes(lastTimestamp + timestampStep * (index + 1), frequencies[index]);
    }

    this.commitFrequencies();
  };

  private rewindToCommitted = () => {
    this.frequencyRecords.length = this.committedRecordCount;
    this.playerNotes.length = this.committedNoteCount;

    // Appending mutates the note on the end - its length, its own records, and the hold judged off
    // them - so truncating alone would leave the guesses baked into the note they landed in.
    if (this.committedBoundaryNote && this.committedNoteCount > 0) {
      this.playerNotes[this.committedNoteCount - 1] = {
        ...this.committedBoundaryNote,
        frequencyRecords: [...this.committedBoundaryNote.frequencyRecords],
      };
    }
  };

  private commitFrequencies = () => {
    this.committedRecordCount = this.frequencyRecords.length;
    this.committedNoteCount = this.playerNotes.length;

    const boundary = this.playerNotes.at(-1);
    this.committedBoundaryNote = boundary ? { ...boundary, frequencyRecords: [...boundary.frequencyRecords] } : null;
  };

  private dispatchSectionUpdate = () => {
    const currentSectionIndex = this.getCurrentSectionIndex();
    if (this.storedSectionIndex !== currentSectionIndex) {
      events.sectionChange.dispatch(this.number, this.storedSectionIndex);
      this.storedSectionIndex = currentSectionIndex;
    }
  };

  public updatePlayerNotes = (timestamp: number, frequency: number) => {
    const record = { timestamp, frequency };
    this.frequencyRecords.push(record);

    const recordBeat = record.timestamp / this.gameState.getSongBeatLength();
    const recordSection = this.getSectionByBeat(recordBeat);

    if (isNotesSection(recordSection)) {
      const note = getNoteAtBeat(recordSection, recordBeat, 0) ?? getNoteAtBeat(recordSection, recordBeat, 0.5);

      if (note)
        appendFrequencyToPlayerNotes(
          this.playerNotes,
          record,
          note,
          this.gameState.getSongBeatLength(),
          this.gameState.getTolerance(),
        );
    }
  };

  public getPlayerNotes = () => this.playerNotes;
  public getPlayerFrequencies = () => this.frequencyRecords;

  public getSectionIndexByBeat = (beat: songBeat) => {
    return this.getTrack().sections.findIndex((section, index, sections) => {
      if (beat < 0) return true;
      if (beat < section.start) return false;
      if (index === sections.length - 1) return true;
      return sections[index + 1].start > beat;
    });
  };

  public getNoteAtBeat = (beat: songBeat) => {
    const section = this.getSectionByBeat(beat);
    if (!section || !isNotesSection(section)) return undefined;
    return getNoteAtBeat(section, beat, 0);
  };

  public getCurrentSectionIndex = () => this.getSectionIndexByBeat(this.gameState.getCurrentBeat());
  public getCurrentNote = () => this.getNoteAtBeat(this.gameState.getCurrentBeat());

  public getSectionByBeat = (beat: number) => this.getTrack().sections[this.getSectionIndexByBeat(beat)] ?? null;

  public getCurrentSection = () => {
    const sectionIndex = this.getCurrentSectionIndex();

    return this.getTrack().sections[sectionIndex] ?? null;
  };

  public getNextSection = (index = 1) => {
    const sectionIndex = this.getCurrentSectionIndex();

    return sectionIndex > -1 ? (this.getTrack().sections[sectionIndex + index] ?? null) : null;
  };

  public getPreviousSection = (index = 1) => {
    const sectionIndex = this.getCurrentSectionIndex();

    return sectionIndex > -1 ? (this.getTrack().sections[sectionIndex - index] ?? null) : null;
  };

  public getLastNotesSection = () => {
    const last = this.getTrack().sections.at(-1);
    return isNotesSection(last) ? last : (this.getTrack().sections.at(-2) as NotesSection);
  };

  // Both read `getTrack()`, the same object the notes were captured against - at any player count
  // but two that is the merged track, and scoring against `tracks[getTrackIndex()]` instead measured
  // the sung beats of one track against the maximum of another.
  //
  // Scoped to this singer's own turns, the same scoping the combo and the note stats already used -
  // in pass the mic the two disagreed, and a flawless turn read 43% and a grade of D beside an
  // accuracy of 100%. `undefined` outside pass the mic, where every section is theirs.
  public getScore = () =>
    calculateScore(
      this.playerNotes,
      this.getTrack(),
      this.gameState.getTolerance(),
      this.gameState.getSectionOwnership(this.number),
    );
  public getDetailedScore = () =>
    calculateDetailedScoreData(
      this.playerNotes,
      this.getTrack(),
      this.gameState.getTolerance(),
      this.gameState.getSectionOwnership(this.number),
    );

  public getMinPitch = () => this.min;
  public getMaxPitch = () => this.max;

  public getTrackIndex = () =>
    this.gameState.getSingSetup()!.players.find((player) => player.number === this.number)!.track;
  public getTrack = () => {
    if (this.gameState.isMergedTrack()) {
      return this.gameState.getSong()!.mergedTrack!;
    } else {
      return this.gameState.getSong()!.tracks[this.getTrackIndex()];
    }
  };

  public resetNotes = () => {
    this.playerNotes = [];
    // The note checkpoint has to go with them or the next pack rewinds to a length this array no
    // longer has. The record checkpoint stays, because the records themselves are not cleared here.
    this.committedNoteCount = 0;
    this.committedBoundaryNote = null;
  };
}

export default PlayerState;
